import type { PeopleOption } from "./PeopleMultiSelect";

type PeopleOptionId = string | number | null | undefined;

export type PeopleOptionSelectors<T> = Readonly<{
  id: (source: T) => PeopleOptionId;
  displayName: (source: T) => string | null | undefined;
  subtitle?: (source: T) => string | null | undefined;
}>;

export type CustomerOwnerSource = Readonly<{
  id?: string | number | null;
  displayName?: string | null;
  email?: string | null;
  isSelectable?: boolean | null;
}>;

export type AssignedToSource = Readonly<{
  entraId?: string | null;
  name?: string | null;
}>;

export function mapToPeopleOption<T>(
  source: T,
  selectors: PeopleOptionSelectors<T>,
): PeopleOption {
  const subtitle = selectors.subtitle?.(source);

  return {
    id: selectors.id(source)?.toString() ?? "",
    displayName: selectors.displayName(source) ?? "",
    ...(selectors.subtitle ? { subtitle: subtitle ?? undefined } : {}),
  };
}

export function mapCustomerOwnerToPeopleOption(
  owner: CustomerOwnerSource,
): PeopleOption {
  return mapToPeopleOption(owner, {
    id: ({ id }) => id,
    displayName: ({ displayName }) => displayName ?? "Unknown",
    subtitle: ({ email }) => email,
  });
}

// Only selectable owners go in the dropdown; the rest (inactive users and legacy contact-id aliases)
// are kept solely to name values that were already saved.
export function partitionCustomerOwnerOptions(
  owners: readonly CustomerOwnerSource[],
): Readonly<{ options: PeopleOption[]; readOnlyOptions: PeopleOption[] }> {
  const options: PeopleOption[] = [];
  const readOnlyOptions: PeopleOption[] = [];

  owners.forEach((owner) => {
    const option = mapCustomerOwnerToPeopleOption(owner);
    if (option.id === "") {
      return;
    }
    (owner.isSelectable === true ? options : readOnlyOptions).push(option);
  });

  return { options, readOnlyOptions };
}

export function mapAssignedToToPeopleOption(
  assignedTo: AssignedToSource,
): PeopleOption {
  return mapToPeopleOption(assignedTo, {
    id: ({ entraId }) => entraId?.trim(),
    displayName: ({ name }) => name?.trim(),
  });
}
